import { supabase } from './supabase'

/**
 * Ürün fotoğrafları (Görselli menü modülü), Supabase'in "menu" kovasında.
 *
 * Telefonla çekilen fotoğraf 3-5 MB; QR menüyü mobil veriyle açan müşteri
 * için ağır, kova için de israf. Yüklemeden önce tarayıcıda küçültülüyor:
 * en uzun kenar 1000 px, JPEG. Sonuç çoğunlukla 100-200 KB.
 *
 * Her yükleme yeni bir dosya adı alıyor (üzerine yazma yok): tarayıcılar ve
 * CDN eski fotoğrafı önbellekte tutuyordu, adı değişince sorun kalmıyor.
 */
const BUCKET = 'menu'
const MAX_SIDE = 1000

function client() {
  if (!supabase) throw new Error('Bağlantı yok.')
  return supabase
}

/** Dosyayı küçültüp JPEG'e çevirir. Açılamayan biçimde (masaüstünde HEIC) hata. */
async function shrink(file: File): Promise<Blob> {
  const url = URL.createObjectURL(file)
  try {
    const img = await new Promise<HTMLImageElement>((resolve, reject) => {
      const el = new Image()
      el.onload = () => resolve(el)
      el.onerror = () => reject(new Error('Bu fotoğraf açılamadı. JPG ya da PNG seçin.'))
      el.src = url
    })
    const scale = Math.min(1, MAX_SIDE / Math.max(img.naturalWidth, img.naturalHeight))
    const w = Math.round(img.naturalWidth * scale)
    const h = Math.round(img.naturalHeight * scale)
    const canvas = document.createElement('canvas')
    canvas.width = w
    canvas.height = h
    const ctx = canvas.getContext('2d')
    if (!ctx) throw new Error('Fotoğraf işlenemedi.')
    // Şeffaf PNG siyaha dönmesin.
    ctx.fillStyle = '#fff'
    ctx.fillRect(0, 0, w, h)
    ctx.drawImage(img, 0, 0, w, h)
    return await new Promise<Blob>((resolve, reject) =>
      canvas.toBlob((b) => (b ? resolve(b) : reject(new Error('Fotoğraf işlenemedi.'))), 'image/jpeg', 0.82),
    )
  } finally {
    URL.revokeObjectURL(url)
  }
}

/** Fotoğrafı küçültüp yükler, herkese açık adresini döner. */
export async function uploadMenuImage(tenantId: string, productId: string, file: File): Promise<string> {
  const blob = await shrink(file)
  const path = `${tenantId}/${productId}-${crypto.randomUUID().slice(0, 8)}.jpg`
  const { error } = await client().storage.from(BUCKET).upload(path, blob, {
    contentType: 'image/jpeg',
    cacheControl: '31536000',
  })
  if (error) throw new Error(error.message)
  return client().storage.from(BUCKET).getPublicUrl(path).data.publicUrl
}

/**
 * Artık kullanılmayan fotoğrafı siler (değiştirilen ya da kaldırılan). Başarısız
 * olursa sessiz: kovada sahipsiz bir dosya kalır, ürün kaydı yine doğru.
 */
export async function deleteMenuImage(url: string | null | undefined): Promise<void> {
  if (!url) return
  const marker = `/object/public/${BUCKET}/`
  const i = url.indexOf(marker)
  if (i < 0) return
  try {
    await client().storage.from(BUCKET).remove([decodeURIComponent(url.slice(i + marker.length))])
  } catch {
    /* sahipsiz dosya kalır */
  }
}
